import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import React, { useRef } from 'react'

import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useReviewSystemV3Experiment from '@src/quince-site-common/src/hooks/useReviewSystemV3Experiment'
import { usePerReviewVisibility } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import { IFilterProps } from '@src/quince-site-pdp/src/organisms/productPageSections/productReviews/types'
import { ProductProperties } from '@src/telemetry/getProductPropertiesForTracking'

// Utils

import LoaderWrapper from '../../loaderWrapper'
import Pagination, { PaginationProps } from '../../pagination'
import { ReviewItem, ReviewItemProps } from '../reviewItem'

import styles from './allReviews.module.scss'
import { ReviewsFilter } from './ReviewsFilter'
import { useReviewFadeTransition } from './useReviewFadeTransition'

export interface AllReviewsProps {
  reviews: ReviewItemProps[]
  paginationProps: PaginationProps
  className?: string
  filterProps: IFilterProps
  showReviewsLoader?: boolean
  onReviewImageClick: (reviewId: number, imageIndex: number) => void
  isKidsAndBabyProduct?: boolean
  variants?: any
  productProperties: ProductProperties
  newPdpv2?: boolean
  reviewSystemExp: ReturnType<typeof useReviewSystemV3Experiment>
  totalReviewsCount: number
  hasReviewGalleryPhotos: boolean
  /** URL for "Learn more" in reward programme tooltip (from locale config, e.g. Gladly HC article). */
  rewardProgrammeLearnMoreUrl?: string
  onReviewsBottomVisible?: (review_count: number, page_number: number) => void
}

export const AllReviews = ({ className = '', reviewSystemExp, ...props }: AllReviewsProps) => {
  const { t } = useTranslation('common')
  const { deviceType } = useDeviceInfo()
  const { isB2BSelfService } = useStorefrontModeContext()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE

  // Fade animation enabled when review summary and filter tags are shown
  const isFadeEnabled = reviewSystemExp.showReviewSummaryAndFilterTags
  // FADE_MS must match the CSS transition duration defined in allReviews.module.scss
  const FADE_MS = isMobile ? 200 : 250

  const { isFirstLoad, isContainerVisible, preservedMinHeight, displayedReviews, containerRef } =
    useReviewFadeTransition({
      isLoading: props.showReviewsLoader,
      isFadeEnabled,
      fadeDurationMs: FADE_MS,
      reviews: props.reviews,
      count: props.filterProps.filterBy.count,
    })

  const reviewRefs = useRef<(HTMLDivElement | null)[]>([])

  usePerReviewVisibility({
    reviewWrapperRefs: reviewRefs,
    reviewCount: displayedReviews.length,
    pageNumber: props.paginationProps.page,
    onReviewVisible: props.onReviewsBottomVisible,
  })

  return (
    <div
      className={classnames(styles.allReviewWrap, className, props.newPdpv2 && styles.allReviewWrap_newPdpDesignV2, {
        [styles.allReviewWrap_b2bSelfServe as string]: isB2BSelfService,
      })}
    >
      <ReviewsFilter
        {...props.filterProps}
        variants={props.variants}
        productProperties={props.productProperties}
        newPdpv2={props.newPdpv2}
        reviewSystemExp={reviewSystemExp}
        totalReviewsCount={props.totalReviewsCount}
        hasReviewGalleryPhotos={props.hasReviewGalleryPhotos}
      />
      <LoaderWrapper showLoader={isFadeEnabled ? isFirstLoad && !!props.showReviewsLoader : !!props.showReviewsLoader}>
        {props.filterProps.hasPhoto && displayedReviews.length <= 0 ? (
          <div className={styles.allReviews__noPhotoReviewText}>{t('no_reviews_with_photos')}</div>
        ) : (
          <div
            ref={containerRef}
            data-testid="reviews-inner-wrap"
            className={classnames(styles.reviewInnerWrap, {
              [styles.reviewInnerWrap_newPdpv2 ?? '']: props.newPdpv2,
              [styles.reviewInnerWrap_fadeVisible ?? '']: isFadeEnabled && isContainerVisible,
              [styles.reviewInnerWrap_fadeHidden ?? '']: isFadeEnabled && !isContainerVisible,
            })}
            style={preservedMinHeight ? { minHeight: preservedMinHeight } : undefined}
          >
            {displayedReviews.map((reviewItem: ReviewItemProps, index: number) => (
              <div
                key={reviewItem.reviewId}
                data-review-index={index}
                ref={el => {
                  reviewRefs.current[index] = el
                }}
                className={props.newPdpv2 ? styles.reviewItemWrap_newPdpv2 : ''}
              >
                <ReviewItem
                  index={index + (props.paginationProps.page - 1) * 4}
                  {...reviewItem}
                  isKidsAndBabyProduct={props.isKidsAndBabyProduct}
                  onReviewImageClick={props.onReviewImageClick}
                  newPdpv2={props.newPdpv2}
                  rewardProgrammeLearnMoreUrl={props.rewardProgrammeLearnMoreUrl}
                />
              </div>
            ))}
          </div>
        )}
      </LoaderWrapper>

      {props.reviews.length > 0 && <Pagination {...props.paginationProps} />}
    </div>
  )
}

export default AllReviews
